/**
 * Peças de desenho repetidas na abertura (E126.F3): o cartão com título, a linha rótulo–valor e o
 * aviso com título. Sem cor como única pista: o aviso leva título e texto, e o tom vai em
 * `data-tom`.
 *
 * E163.F2 (GUS-209 A67-03/17): o cartão é o card do HTML 6.7 — borda, superfície e o título em mono,
 * maiúsculo e espaçado, que é um heading de verdade, e não um rótulo pequeno.
 */
import { useId, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * O `.btn` do rodapé da jornada no HTML 6.7 (E163.F2): 14px semibold, 9px por 14px, 36px de altura.
 * Em px porque a raiz da aplicação é 14px e o `Button` padrão sai menor que o molde.
 */
export const BOTAO_DO_RODAPE =
  'h-auto min-h-[36px] px-[14px] py-[9px] font-display text-[14px] font-semibold';

/** A barra do rodapé da jornada (`.journey-footer` do molde): Voltar à esquerda, a ação à direita. */
export const RODAPE_DA_JORNADA =
  'mt-[6px] flex flex-wrap items-center justify-between gap-5 border-t border-border pt-5';

/** O marcador do `summary` do molde: ▶ recolhido, ▼ aberto. Decorativo; o estado vai no
 * `aria-expanded` do botão. */
export function MarcadorDoRecolhivel({ aberto }: { aberto: boolean }): JSX.Element {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block text-[9px] leading-none transition-transform',
        aberto && 'rotate-90',
      )}
    >
      ▶
    </span>
  );
}

/**
 * O bloco recolhível do molde (`details`/`summary`): começa recolhido quando o chamador quer, e o
 * recolhido é `hidden` — fora da tela e do foco, sem desmontar o que foi digitado (GUS-202).
 */
export function Recolhivel({
  titulo,
  aberto,
  onAberto,
  testId,
  children,
}: {
  titulo: string;
  aberto: boolean;
  onAberto: (aberto: boolean) => void;
  testId: string;
  children: ReactNode;
}): JSX.Element {
  const painelId = useId();
  return (
    <div data-testid={testId} data-open={aberto}>
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        aria-expanded={aberto}
        aria-controls={painelId}
        onClick={() => onAberto(!aberto)}
        className="flex items-center gap-sc-2 text-[14px] text-accent-700 underline-offset-2 hover:underline"
      >
        <MarcadorDoRecolhivel aberto={aberto} />
        {titulo}
      </button>
      <div id={painelId} hidden={!aberto} className="mt-sc-3 flex flex-col gap-sc-4">
        {children}
      </div>
    </div>
  );
}

export function CartaoDaAbertura({
  titulo,
  testId,
  className,
  children,
}: {
  titulo: string;
  testId?: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const tituloId = useId();
  return (
    <section
      aria-labelledby={tituloId}
      data-testid={testId}
      className={cn('border border-border bg-surface p-[22px]', className)}
    >
      {/* E165 (HTML 6.8): o `.card-title` em IBM Plex Mono 13px com 0.08em. */}
      <h2
        id={tituloId}
        className="mb-[18px] font-titulo text-[13px] font-medium uppercase leading-[1.55] tracking-[0.08em] text-accent-700"
      >
        {titulo}
      </h2>
      {children}
    </section>
  );
}

export function ParDeDado({
  rotulo,
  children,
  testId,
}: {
  rotulo: string;
  children: ReactNode;
  testId?: string;
}): JSX.Element {
  return (
    <div
      className="grid grid-cols-1 gap-1 border-b border-border py-sc-2 last:border-b-0 sm:grid-cols-[180px_1fr]"
      data-testid={testId}
    >
      <span className="text-body-sm text-fg-muted">{rotulo}</span>
      <span className="text-body-sm font-semibold text-fg">{children}</span>
    </div>
  );
}

// E163.F2: o `.notice` do HTML 6.7 — fundo e borda dão o tom; o texto fica na cor do corpo.
const TOM = {
  info: 'border-accent-200 bg-accent-bg',
  aviso: 'border-warning/30 bg-warning-50',
} as const;

export function AvisoDaAbertura({
  tom,
  titulo,
  texto,
  testId,
  children,
}: {
  tom: keyof typeof TOM;
  titulo: string;
  texto?: string;
  testId: string;
  /** E163.F3: o aviso com conteúdo próprio (lista e ação), no lugar do texto. */
  children?: ReactNode;
}): JSX.Element {
  return (
    <div
      role="status"
      data-testid={testId}
      data-tom={tom}
      className={cn('border px-[14px] py-3 text-[14px] text-fg', TOM[tom])}
    >
      <p className="mb-[3px] font-display text-[15px] font-semibold">{titulo}</p>
      {texto !== undefined ? <p>{texto}</p> : null}
      {children}
    </div>
  );
}
